import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Zap, Layers } from "lucide-react";

function formatErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}

export default function StaffPortal() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user, ready } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (ready && user) {
      nav(user.role === "admin" ? "/admin/tables" : "/staff", { replace: true });
    }
  }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name || u.email}`);
      const dest = loc.state?.from?.pathname || (u.role === "admin" ? "/admin/tables" : "/staff");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="staff-scope min-h-screen flex">
      <div className="hidden md:flex flex-1 border-r border-[#262626] p-14 flex-col justify-between">
        <div>
          <div className="overline text-zinc-500">Restaurant OS · staff site</div>
          <h1 className="font-condensed font-bold text-6xl tracking-tight leading-none mt-4">
            COMMAND<br />CENTER
          </h1>
          <p className="text-zinc-400 max-w-sm mt-6 font-plex text-sm leading-relaxed">
            Live requests, kitchen orders, tables and menu control — one dense,
            keyboard-first console. Sign in to get to work.
          </p>
        </div>

        <ul className="space-y-5 max-w-sm">
          {[
            { icon: Zap, t: "Live requests", d: "See waiter, check and order pings as they land." },
            { icon: Layers, t: "Full control", d: "Manage tables, print QR codes, edit the menu." },
            { icon: ShieldCheck, t: "Role-based access", d: "Staff and admin have the right permissions." },
          ].map((f) => (
            <li key={f.t} className="flex gap-4">
              <div className="w-10 h-10 border border-[#262626] bg-[#121212] inline-flex items-center justify-center shrink-0">
                <f.icon size={16} className="text-[#007aff]" />
              </div>
              <div>
                <div className="font-plex text-sm text-white">{f.t}</div>
                <div className="font-plex text-xs text-zinc-400 mt-1">{f.d}</div>
              </div>
            </li>
          ))}
        </ul>

        <div className="text-zinc-600 font-plex text-xs">v1.0 · secure JWT session</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="overline text-zinc-500">{t("sign_in")}</div>
          <h2 className="font-condensed font-bold text-4xl tracking-tight mt-2">{t("staff_access")}</h2>
          <p className="text-zinc-500 mt-2 font-plex text-sm">{t("use_account")}</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="overline text-zinc-500">{t("email")}</label>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-3 py-3 font-plex text-sm"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="overline text-zinc-500">{t("password")}</label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 px-3 py-3 font-plex text-sm"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="mt-8 w-full bg-white text-black py-3 font-plex text-sm font-medium hover:bg-[#007aff] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? t("signing_in") : (<>{t("sign_in")} <ArrowRight size={16} /></>)}
          </button>

          <p className="mt-8 font-plex text-xs text-zinc-600">
            This is the staff site. Guests should scan the QR on their table instead.
          </p>
        </form>
      </div>
    </div>
  );
}
